// =============================================================================
// FILE:
// frontend/src/pms/task_execution/drawers/TaskExecutionDrawer.js
// =============================================================================

import React, {
  useEffect,
  useState
} from "react";

import {
  X,
  Maximize2,
  Minimize2
} from "lucide-react";

import OverviewTab
from "../tabs/OverviewTab";

import MaterialsTab
from "../tabs/MaterialsTab";

import RequestsTab
from "../tabs/RequestsTab";

import IssuesTab
from "../tabs/IssuesTab";

import TimelineTab
from "../tabs/TimelineTab";

import ExecutionTab
from "../tabs/ExecutionTab";


// =============================================================================
// TABS
// =============================================================================

const tabs = [

  {
    key: "overview",
    label: "Overview"
  },

  {
    key: "materials",
    label: "Materials"
  },

  {
    key: "requests",
    label: "Requests"
  },

  {
    key: "issues",
    label: "Issues"
  },

  {
    key: "timeline",
    label: "Timeline"
  },

  {
    key: "execution",
    label: "Execution"
  }
];



// =============================================================================
// COMPONENT
// =============================================================================

export default function TaskExecutionDrawer({

  open,

  task,

  onClose

}) {


  // ===========================================================================
  // STATE
  // ===========================================================================
  const [activeTab, setActiveTab] =
    useState("overview");


  const [isFullscreen, setIsFullscreen] =
    useState(false);

  // ===========================================================================
// LOCK BODY SCROLL
// ===========================================================================
useEffect(() => {

  if (open) {

    document.body.style.overflow =
      "hidden";

  } else {

    document.body.style.overflow =
      "auto";
  }

  return () => {

    document.body.style.overflow =
      "auto";
  };

}, [open]);  

  // ===========================================================================
  // CLOSE
  // ===========================================================================
  if (!open || !task) {
    return null;
  }


  // ===========================================================================
  // TAB RENDER
  // ===========================================================================
  const renderTab =
    () => {

      switch (activeTab) {

        case "overview":

          return (
            <OverviewTab
              task={task}
            />
          );

        case "materials":

          return (
            <MaterialsTab
              task={task}
            />
          );

        case "requests":

          return (
            <RequestsTab
              task={task}
            />
          );

        case "issues":

          return (
            <IssuesTab
              task={task}
            />
          );

        case "timeline":

          return (
            <TimelineTab
              task={task}
            />
          );

        case "execution":

          return (
            <ExecutionTab
              task={task}
            />
          );

        default:

          return null;
      }
    };


  // ===========================================================================
  // RENDER
  // ===========================================================================
  return (

    <div style={styles.overlay}>


      <div
        style={{

          ...styles.drawer,

          width:
            isFullscreen
              ? "100%"
              : "88%"
        }}
      >


        {/* ================================================================ */}
        {/* HEADER */}
        {/* ================================================================ */}
        <div style={styles.header}>


          {/* ============================================================ */}
          {/* LEFT */}
          {/* ============================================================ */}
          <div>


            <div style={styles.title}>

              Task Execution Workspace

            </div>


            <div style={styles.subtitle}>


              {

                task.task_name ||

                "Task"
              }


              {" • "}


              {

                task.phase_name ||

                "Phase"
              }


            </div>

          </div>


          {/* ============================================================ */}
          {/* ACTIONS */}
          {/* ============================================================ */}
          <div style={styles.actions}>


            <button
              style={styles.iconBtn}
              onClick={() =>

                setIsFullscreen(
                  !isFullscreen
                )
              }
            >

              {

                isFullscreen

                  ?

                  <Minimize2 size={18} />

                  :

                  <Maximize2 size={18} />
              }

            </button>


            <button
              style={styles.iconBtn}
              onClick={onClose}
            >

              <X size={18} />

            </button>

          </div>

        </div>


        {/* ================================================================ */}
        {/* TASK SUMMARY BAR */}
        {/* ================================================================ */}
        <div style={styles.summaryBar}>


          <div style={styles.summaryItem}>


            <div style={styles.summaryLabel}>

              Readiness

            </div>


            <div style={styles.summaryValue}>


              {

                task.readiness_percent || 0

              }%

            </div>

          </div>


          <div style={styles.summaryItem}>


            <div style={styles.summaryLabel}>

              Status

            </div>


            <div style={styles.summaryValue}>


              {

                task.execution_status ||

                "NOT_STARTED"
              }

            </div>

          </div>


          <div style={styles.summaryItem}>


            <div style={styles.summaryLabel}>

              Pending Requests

            </div>


            <div style={styles.summaryValue}>


              {

                task.pending_requests || 0
              }

            </div>

          </div>


          <div style={styles.summaryItem}>


            <div style={styles.summaryLabel}>

              Priority

            </div>


            <div style={styles.summaryValue}>


              {

                task.priority ||

                "NORMAL"
              }

            </div>

          </div>

        </div>


        {/* ================================================================ */}
        {/* TAB BAR */}
        {/* ================================================================ */}
        <div style={styles.tabBar}>


          {

            tabs.map((tab) => (

              <button

                key={tab.key}

                onClick={() =>
                  setActiveTab(tab.key)
                }

                style={{

                  ...styles.tabBtn,

                  ...(activeTab === tab.key

                    ?

                    styles.activeTab

                    :

                    {})
                }}
              >

                {tab.label}

              </button>
            ))
          }

        </div>


        {/* ================================================================ */}
        {/* BODY */}
        {/* ================================================================ */}
        <div style={styles.body}>


          {renderTab()}

        </div>

      </div>

    </div>
  );
}


// =============================================================================
// STYLES
// =============================================================================

const styles = {

  overlay: {

    position: "fixed",

    top: 0,

    left: 0,

    right: 0,

    bottom: 0,

    background:
      "rgba(0,0,0,0.35)",

    display: "flex",

    justifyContent: "flex-end",

    zIndex: 9999
  },

  drawer: {

    height: "100vh",

    background: "#f5f7fb",

    display: "flex",

    flexDirection: "column",

    transition:
      "width 0.25s ease"
  },

  header: {

    background: "#ffffff",

    padding: "18px 20px",

    borderBottom:
      "1px solid #e5e7eb",

    display: "flex",

    justifyContent: "space-between",

    alignItems: "center"
  },

  title: {

    fontSize: 22,

    fontWeight: 700,

    color: "#111827"
  },

  subtitle: {

    marginTop: 6,

    fontSize: 13,

    color: "#6b7280"
  },

  actions: {

    display: "flex",

    gap: 10
  },

  iconBtn: {

    width: 36,

    height: 36,

    borderRadius: 8,

    border: "1px solid #d1d5db",

    background: "#fff",

    display: "flex",

    alignItems: "center",

    justifyContent: "center",

    cursor: "pointer"
  },

  summaryBar: {

    background: "#ffffff",

    borderBottom:
      "1px solid #e5e7eb",

    padding: "14px 20px",

    display: "flex",

    gap: 30
  },

  summaryItem: {

    display: "flex",

    flexDirection: "column"
  },

  summaryLabel: {

    fontSize: 12,

    color: "#6b7280",

    marginBottom: 4
  },

  summaryValue: {

    fontSize: 15,

    fontWeight: 700,

    color: "#111827"
  },

  tabBar: {

  position: "sticky",

  top: 0,

  zIndex: 20,

  background: "#ffffff",

  display: "flex",

  borderBottom:
    "1px solid #e5e7eb",

  padding: "0 14px"
},

  tabBtn: {

    height: 48,

    padding: "0 18px",

    border: "none",

    background: "transparent",

    cursor: "pointer",

    fontWeight: 600,

    color: "#6b7280",

    borderBottom:
      "3px solid transparent"
  },

  activeTab: {

    color: "#2563eb",

    borderBottom:
      "3px solid #2563eb"
  },

  body: {

    flex: 1,

    overflow: "auto",
    overflowX: "hidden",

    padding: 20
  }
};